/* eslint-disable */
import { createSelector } from '@reduxjs/toolkit';
import { InfiniteData } from '@reduxjs/toolkit/query';
import { TypeUtils } from '@vectord/utils';

interface Association {
  type: string;
  id: string | number;
}

export interface NormalizedData {
  id: string | number;
  [key: string]: any; // Allow additional properties
}

interface EntityData {
  associations?: Record<string, Association | Association[] | null>;
  [key: string]: any; // Allow additional properties
}

interface ApiResponse<T> {
  data: T;
  associations: Record<string, Record<string, EntityData>>;
}

export function normalizedAssociation(
  association: Association,
  associations: Record<string, Record<string, EntityData>>
): NormalizedData {
  const { type, id } = association;
  const data = associations[type]?.[id];
  if (data) {
    // @typescript-eslint/no-use-before-define
    return normalizedSingleEntity(data, associations);
  } else {
    // If association data is not sent then simply return a object with id key
    const _id = typeof id === 'string' ? parseInt(id, 10) : id;
    return { id: Number.isNaN(_id) ? id : _id };
  }
}

export function normalizedSingleEntity(
  data: EntityData,
  associations: Record<string, Record<string, EntityData>>
): NormalizedData {
  const { associations: entityAssociations, ...baseData } = data;
  // @ts-ignore
  const normalizedData: NormalizedData = baseData;

  Object.entries(entityAssociations || {}).forEach(([k, v]) => {
    if (TypeUtils.isNullOrUndefined(v)) {
      normalizedData[k] = null;
    } else if (Array.isArray(v)) {
      normalizedData[k] = v.map((o) => normalizedAssociation(o, associations));
    } else {
      normalizedData[k] = normalizedAssociation(v!, associations);
    }
  });

  return normalizedData;
}

export function normalizedResponseSelector<T>(
  data: T | T[] | null | undefined,
  associations: Record<string, Record<string, EntityData>>
): NormalizedData | NormalizedData[] | null {
  if (Array.isArray(data)) {
    return data.map((o) => normalizedSingleEntity(o as any, associations));
  } else if (!TypeUtils.isNullOrUndefined(data)) {
    return normalizedSingleEntity(data as EntityData, associations);
  } else {
    return null;
  }
}

/**
 * `createNormalizedResponseSelector` is a selector that normalizes the response
 * of a json api request. It takes the response data and associations
 * and returns a normalized response. This is useful for reducers that need to
 * handle the response data.
 */
export default function createNormalizedResponseSelector<T>(
  baseSelector: (state: any) => ApiResponse<T> | undefined,
  options: { isList?: boolean } = {}
) {
  const { isList = false } = options;
  const defaultState = {
    data: isList ? [] : null,
    associations: {},
  };

  const dataSelector = (state: any) => {
    const response = baseSelector(state) || defaultState;
    return response.data;
  };

  const associationsSelector = (state: any) => {
    const response = baseSelector(state) || defaultState;
    return response.associations;
  };

  return createSelector(
    dataSelector,
    associationsSelector,
    normalizedResponseSelector,
  );
}

export const infiniteQueryCombinedDataSelector = <T extends unknown[]>() => createSelector(
  (state: InfiniteData<T, any> | undefined) => state,
  (data): T[number][] | undefined => data?.pages?.flat(),
);
